import { Fragment } from 'react';

import { MatchupByRound, TournamentMatchups } from '~/../api/common.types';
import Section from '~/components/hero/LandingPage/Section';
import Matchup from '~/components/war/tournament/Matchup';

function generateVersusTitle(lastMatchup: MatchupByRound): null | string {
  if (lastMatchup.round !== 8) {
    return null;
  }
  const winners = lastMatchup.matchups.flatMap((matchup) => matchup).filter((warrior) => warrior.isWinner);
  if (winners.length === 2) {
    return `${winners[0].name} vs ${winners[1].name}`;
  }
  return null;
}

const fillBrackets = (num: number, right: boolean) => {
  const matches: JSX.Element[] = [];

  for (let i = num; i > 0; i--) {
    matches.push(<Fragment key={`${i}-fill-bracket`}>
      <div className="bracket__spacer"></div>
      <div className="bracket__matchup"><span className="bracket__future">1 / {num * 2 * 2 * 2}</span></div>
      <div className="bracket__matchup"><span className="bracket__future"></span></div>
      <div className={`bracket__spacer bracket__spacer--connector${right ? '-left' : ''}`}></div>
      <div className="bracket__matchup"><span className="bracket__future">1 / {num * 2 * 2 * 2}</span></div>
      <div className="bracket__matchup"><span className="bracket__future"></span></div>
    </Fragment>);
  }
  return matches;
};


function BracketSpacer({
  index,
  right,
}: {
  index: number,
  right: boolean,
}) {
  let connectorClass = '';
  if (index % 2) {
    connectorClass = ' bracket__spacer--connector';
    if (right) {
      connectorClass += '-left';
    }
  }
  return (
    <div className={`bracket__spacer${connectorClass}`}></div>
  );
}


function BracketMatchup({
  bracketMatchup,
  ownedWarriorIDs,
  index,
  right,
}: {
  bracketMatchup: TournamentMatchups;
  ownedWarriorIDs: number[] | undefined;
  index: number;
  right: boolean;
}) {
  return (
    <>
      <BracketSpacer index={index} right={right} />
      <div className="bracket__matchup">
        <Matchup
          matchup={bracketMatchup}
          ownedWarriorIDs={ownedWarriorIDs}
          isBracket={true}
          right={right}
        />
      </div>
    </>
  );
}

function IndividualBracket({
  round,
  ownedWarriorIDs,
  right,
}: {
  round: MatchupByRound;
  ownedWarriorIDs: number[] | undefined;
  right: boolean;
}) {
  if (!round.matchups || !round.matchups.length) return <></>;

  let bracketMatchups;
  if (right) {
    bracketMatchups = round.matchups.slice(round.matchups.length / 2);
  } else {
    bracketMatchups = round.matchups.slice(0, round.matchups.length / 2);
  }
  return (
    <div className="bracket__round">
      {bracketMatchups.map((matchup: any, i) => {
        const [warriorOne, warriorTwo] = matchup;
        return (
          <BracketMatchup
            key={`${i}-${warriorOne.id}-${warriorTwo.id}`}
            bracketMatchup={matchup}
            ownedWarriorIDs={ownedWarriorIDs}
            index={i}
            right={right} />
        );
      })}
      <BracketSpacer index={bracketMatchups.length} right={right} />
    </div>
  );
}


export default function Bracket({
  matchupsByRound,
  ownedWarriorIDs,
}: {
  matchupsByRound: MatchupByRound[];
  ownedWarriorIDs: number[] | undefined;
}) {
  // fill remaining slots with length of last round participants
  const fillRounds = (firstRoundLength: number, lastRoundLength: number, versusTitle: string | null) => {
    const rounds: JSX.Element[] = [];
    let middleLabel = '';
    if (versusTitle) {
      middleLabel = versusTitle;
    }
    for (let i = lastRoundLength / 4 / 2; i >= 1; i = i / 2) {
      rounds.push(<div className="bracket__round bracket__round--empty" key={`leftbracketfill${i}`}>
        {fillBrackets(i, false)}
        <div className="bracket__spacer"></div>
      </div>);
    }
    rounds.push(<div className={`bracket__round bracket__round--empty bracket__round--final bracket__round--final-${firstRoundLength}`} key={'emptyfill0'}>
      <div className="bracket__spacer"></div>
      <div className={`bracket__matchup bracket__matchup--final bracket__matchup--final-${firstRoundLength}`}><span className="bracket__future bracket__future--label">{middleLabel}</span></div>
      <div className="bracket__spacer"></div>
    </div>);
    for (let i = 1; i <= lastRoundLength / 4 / 2; i = i * 2) {
      rounds.push(<div className="bracket__round bracket__round--empty" key={`rightbracketfill${i}`}>
        {fillBrackets(i, true)}
        <div className="bracket__spacer"></div>
      </div>);
    }
    return rounds;
  };

  const firstRoundParticipants = matchupsByRound[0];
  const lastRoundParticipants = matchupsByRound[matchupsByRound.length - 1];
  if (!lastRoundParticipants?.matchups || !lastRoundParticipants?.matchups.length) {
    return <></>;
  }

  const versusTitle = generateVersusTitle(lastRoundParticipants);
  return (
    <>
      <Section className={`landing-page__section--tournament_bracket landing-page__section--tournament_bracket--${firstRoundParticipants.matchups.length}`}>
        <article className={`bracket bracket--${firstRoundParticipants.matchups.length}`}>
          {matchupsByRound.map((round, index) => {
            return <IndividualBracket round={round} ownedWarriorIDs={ownedWarriorIDs} right={false} key={`left-bracket-${index}`} />;
          })}
          { fillRounds(firstRoundParticipants.matchups.length, lastRoundParticipants.matchups.length, versusTitle) }
          {/* need to reverse */}
          {matchupsByRound.slice(0).reverse().map((round, index) => {
            return <IndividualBracket round={round} ownedWarriorIDs={ownedWarriorIDs} right={true} key={`right-bracket-${index}`}/>;
          })}
        </article>
      </Section>
    </>
  );
}
